import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, type ContactFilters, type ContactPage, type ContactRow } from '../lib/api';
import { formatDate } from '../lib/format';

/**
 * Contacts directory — Appendix C.2 Phase 2 deliverable 11.
 *
 * §0.3 table contract: navy header, 11px uppercase labels, zebra rows, #EBF0F8
 * hover, and no gold anywhere on these light surfaces. §0.6: at the base
 * breakpoint the grid becomes a stacked card list instead of scrolling
 * sideways.
 *
 * Layout lives in `CONTACT_COLUMNS` and one row renderer, so a column change is
 * a one-line edit in one place rather than a hunt through markup — this screen
 * has never been reviewed by a person and the first review will move things.
 */

const CONTACT_COLUMNS = [
  { key: 'display_name', label: 'Contact', align: 'left' as const },
  { key: 'company_name', label: 'Company', align: 'left' as const },
  { key: 'category', label: 'Category', align: 'left' as const },
  { key: 'tier', label: 'Tier', align: 'left' as const },
  { key: 'email', label: 'Email', align: 'left' as const },
  { key: 'phone_primary', label: 'Phone', align: 'left' as const },
  { key: 'location', label: 'Location', align: 'left' as const },
  { key: 'projects', label: 'Projects', align: 'right' as const },
  { key: 'updated_at', label: 'Updated', align: 'right' as const },
];

const CATEGORIES = [
  'client',
  'subcontractor',
  'vendor_supplier',
  'inspector',
  'adjuster',
  'institutional',
  'partner',
  'internal',
  'other',
];

const TIER_LABEL: Record<string, string> = {
  A: 'A · active',
  B: 'B · dormant',
  C: 'C · unreachable',
  D: 'D · archived',
};

function labelise(value: string | null): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function ContactsDirectory({
  onOpenContact,
}: {
  onOpenContact?: (contact: ContactRow) => void;
}): JSX.Element {
  const [filters, setFilters] = useState<ContactFilters>({ page: 1 });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState<ContactPage | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (next: ContactFilters) => {
    setBusy(true);
    try {
      setPage(await api.contacts(next));
      setNotice(null);
    } catch (failure) {
      setPage(null);
      if (failure instanceof ApiFailure && failure.status === 403) {
        setNotice(
          `Your account is scoped to ${(failure.body.grantedPillars ?? []).join(', ') || 'no entities'}.`,
        );
      } else {
        setNotice('Contacts could not be loaded.');
      }
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load(filters);
  }, [filters, load]);

  function update(patch: Partial<ContactFilters>): void {
    // Any filter change returns to page 1: keeping the offset would silently show
    // a page that no longer exists for the new result set.
    setFilters((current) => ({ ...current, ...patch, page: patch.page ?? 1 }));
  }

  const rows = page?.contacts ?? [];
  const from = page ? (page.page - 1) * page.pageSize + 1 : 0;
  const to = page ? from + rows.length - 1 : 0;

  return (
    <section aria-labelledby="contacts-heading">
      <h1 id="contacts-heading" className="mb-1 text-h1 font-semibold text-text-primary">
        Contacts
      </h1>
      <p className="mb-4 text-body text-text-secondary">
        {page ? (
          <>
            {page.total} contact{page.total === 1 ? '' : 's'} in scope
            {page.total > 0 ? ` · showing ${from}–${to}` : ''}
            {page.scope.length > 0 ? ` · ${page.scope.join(', ')}` : ''}
          </>
        ) : (
          'Loading…'
        )}
      </p>

      <form
        className="mb-4 flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          update({ q: query.trim() || undefined });
        }}
      >
        <div className="min-w-[16rem] flex-1">
          <label htmlFor="contact-search" className="micro-label mb-1 block text-text-secondary">
            Search name, company or email
          </label>
          <input
            id="contact-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="e.g. Champagne, Roofs by Aspen"
            className="h-11 w-full rounded-card border border-border bg-white px-3 text-body text-text-primary"
          />
        </div>
        <div>
          <label htmlFor="contact-category" className="micro-label mb-1 block text-text-secondary">
            Category
          </label>
          <select
            id="contact-category"
            value={filters.category ?? ''}
            onChange={(event) => update({ category: event.target.value || undefined })}
            className="h-11 rounded-card border border-border bg-white px-3 text-body text-text-primary"
          >
            <option value="">All</option>
            {CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {labelise(category)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="contact-tier" className="micro-label mb-1 block text-text-secondary">
            Tier
          </label>
          <select
            id="contact-tier"
            value={filters.tier ?? ''}
            onChange={(event) => update({ tier: event.target.value || undefined })}
            className="h-11 rounded-card border border-border bg-white px-3 text-body text-text-primary"
          >
            <option value="">All</option>
            {Object.entries(TIER_LABEL).map(([tier, label]) => (
              <option key={tier} value={tier}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn-primary h-11 rounded-card px-4 text-body font-medium">
          Search
        </button>
        {filters.q || filters.tier || filters.category ? (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setFilters({ page: 1 });
            }}
            className="btn-secondary h-11 rounded-card px-4 text-body font-medium"
          >
            Clear
          </button>
        ) : null}
      </form>

      {notice ? (
        <p role="status" className="mb-4 text-body text-warning">
          {notice}
        </p>
      ) : null}

      {page && page.pillarlessHidden > 0 ? (
        <p className="mb-4 rounded-card border border-border bg-white px-3 py-2 text-body text-text-secondary">
          {page.pillarlessHidden} contact{page.pillarlessHidden === 1 ? '' : 's'} carry no entity and are
          therefore outside every scope, including this one. Assigning an entity makes them visible.
        </p>
      ) : null}

      {rows.length === 0 && !busy ? (
        <div className="card rounded-card border border-border bg-white p-6">
          <p className="text-body text-text-secondary">
            No contacts match this filter in the current entity scope.
          </p>
        </div>
      ) : null}

      {/* base breakpoint: stacked cards, no horizontal overflow at 375px (§0.6) */}
      <ul className="grid gap-2 lg:hidden">
        {rows.map((contact) => (
          <li key={contact.id} className="card rounded-card border border-border bg-white p-4">
            <div className="flex items-baseline justify-between gap-3">
              <button
                type="button"
                onClick={() => onOpenContact?.(contact)}
                className="text-left text-h3 font-semibold text-royal underline-offset-2 hover:underline"
              >
                {contact.display_name}
              </button>
              <span className="micro-label text-text-secondary">{contact.pillars.join(' ') || '—'}</span>
            </div>
            {contact.company_name ? (
              <p className="mt-1 text-body text-text-secondary">{contact.company_name}</p>
            ) : null}
            <p className="mt-1 text-body text-text-secondary">
              {labelise(contact.category)} · tier {contact.tier}
            </p>
            <p className="numeric mt-2 text-numeric text-text-secondary">
              {contact.phone_primary ?? '—'}
            </p>
            <p className="text-body text-text-secondary">{contact.email ?? '—'}</p>
          </li>
        ))}
      </ul>

      {/* lg and up: the full grid */}
      {rows.length > 0 ? (
        <div className="hidden overflow-hidden rounded-card border border-border lg:block">
          <table className="w-full border-collapse text-body">
            <caption className="sr-only">Contacts in the current entity scope</caption>
            <thead>
              <tr className="table-header bg-navy text-white">
                {CONTACT_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={`px-3 py-2 text-micro font-medium uppercase ${
                      column.align === 'right' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((contact) => (
                <tr key={contact.id} className="table-row border-t border-border">
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => onOpenContact?.(contact)}
                      className="text-left font-medium text-royal underline-offset-2 hover:underline"
                    >
                      {contact.display_name}
                    </button>
                    {contact.in_review ? (
                      <span className="micro-label ml-2 rounded-card border border-warning px-1 py-0.5 text-warning">
                        In review
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-text-secondary">{contact.company_name ?? '—'}</td>
                  <td className="px-3 py-2">{labelise(contact.category)}</td>
                  <td className="px-3 py-2">{contact.tier}</td>
                  <td className="px-3 py-2 text-text-secondary">{contact.email ?? '—'}</td>
                  <td className="numeric px-3 py-2 text-text-secondary">{contact.phone_primary ?? '—'}</td>
                  <td className="px-3 py-2 text-text-secondary">
                    {[contact.city, contact.state_region].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="numeric px-3 py-2 text-right">{contact.project_count}</td>
                  <td className="numeric px-3 py-2 text-right text-text-secondary">
                    {formatDate(contact.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {page ? (
        <nav
          aria-label="Contacts pagination"
          className="mt-4 flex flex-wrap items-center justify-between gap-3"
        >
          <p className="numeric text-numeric text-text-secondary">
            Page {page.page} of {Math.max(1, Math.ceil(page.total / page.pageSize))} · {page.pageSize} rows
            per page
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page.page <= 1 || busy}
              onClick={() => update({ page: page.page - 1 })}
              className="btn-secondary rounded-card px-4 text-body font-medium disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!page.hasMore || busy}
              onClick={() => update({ page: page.page + 1 })}
              className="btn-secondary rounded-card px-4 text-body font-medium disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </nav>
      ) : null}
    </section>
  );
}
